import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { Footer as HTMLFooter } from '@expo/html-elements';

import {
  Container,
  HStack,
  theme,
  useContainerSpacing,
  useNavbarContainerHeight,
  useResponsive,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useOrganizationLinksFooterContentfulData } from '../../hooks/useOrganizationLinksContentfulData';
import { OrganizationLinksFooterCopyright } from './components/OrganizationLinksFooterCopyright';
import { OrganizationLinksFooterSection } from './components/OrganizationLinksFooterSection';
import { useConditionalLinksRules } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const OrganizationLinksFooter = () => {
  const contentfulFooter = useOrganizationLinksFooterContentfulData();
  const conditionalLinksRules = useConditionalLinksRules();
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  const containerSpacing = useContainerSpacing();
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();
  const { currentBreakpoint, minWidth, match } = useResponsive();

  const footerSpacingStyle = useStyle(
    () => ({ paddingVertical: containerSpacing }),
    [containerSpacing],
  );

  const shouldApplyNavbarPadding = isNavbarV2Enabled && currentBreakpoint.isXS;
  const itemsPerRow = match([1, 2, contentfulFooter.sections.length + 2]);
  const contentStyles = match([undefined, styles.contentMarginSM, undefined]);
  const palette = isNavbarV2Enabled ? 'dark' : 'light';
  const footerStyles = [
    styles.footer,
    isNavbarV2Enabled ? styles.footerNavbarV2 : undefined,
    footerSpacingStyle,
    shouldApplyNavbarPadding ? navbarPaddingBottomStyle : undefined,
  ];

  return (
    <HTMLFooter style={footerStyles}>
      <Container style={styles.container}>
        <HStack itemsPerRow={itemsPerRow} style={contentStyles}>
          {contentfulFooter.sections.map((section) => (
            <OrganizationLinksFooterSection
              key={section.name}
              section={section}
              conditionalLinksRules={conditionalLinksRules}
              palette={palette}
            />
          ))}

          {minWidth.isMD ? <EmptyColumn /> : null}

          {currentBreakpoint.isSM ? null : <OrganizationLinksFooterCopyright palette={palette} />}
        </HStack>

        {currentBreakpoint.isSM ? <OrganizationLinksFooterCopyright palette={palette} /> : null}
      </Container>
    </HTMLFooter>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const EmptyColumn = View; // For Layout with ItemsPerRow.

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    marginTop: 'auto',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.SKY,
  },
  footerNavbarV2: {
    backgroundColor: theme.colors.BROWN,
  },
  container: {
    gap: theme.spacing['8'],
  },
  contentMarginSM: {
    marginRight: '33%',
  },
});
